import React, {useState} from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Divider from '@mui/material/Divider';
import PropTypes from 'prop-types';
import {BrickContext} from 'olympe';
import TwoToneCheckbox from '../../TwoToneCheckbox.jsx';
import {useFieldEditability} from '../utils/useFieldEditability.js';

/**
 * @param {CheckboxWithOptionsAccountDetails.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function CheckboxWithOptionsAccountDetails(props) {
    const {isDisabled} = useFieldEditability(props.prTag, {context: props.context});
    const bulkSelectDisabled = isDisabled('accountDetails.bulkSelect');
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentPageCheckbox, setCurrentPageCheckbox] = useState(false);
    const [allPagesCheckbox, setAllPagesCheckbox] = useState(false);

    let totalAccounts = 0;
    for (const materialDetail of props.accountDetailsByMaterial) {
        totalAccounts += materialDetail.accounts.length;
    }

    const uniqueAccountIdentifiers = {};
    for (const selectedAccount of props.selectedAccounts) {
        const key = selectedAccount.accountId + '-' + selectedAccount.materialId;
        uniqueAccountIdentifiers[key] = true;
    }
    const uniqueSelectedAccounts = new Set(props.selectedAccounts.map((account) => `${account.accountId}-${account.materialId}`));

    const areAllAccountsSelected = uniqueSelectedAccounts.size === totalAccounts;

    const toggleMenu = (event) => {
        if (anchorEl) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleNestedCheckboxChange = (option) => {
        if (option === 'current') {
            if (allPagesCheckbox) {
                setAllPagesCheckbox(false);
            }
            if (!currentPageCheckbox) {
                selectCurrentPageAccounts();
                setCurrentPageCheckbox(true);
            } else {
                deselectCurrentPageAccounts();
                setCurrentPageCheckbox(false);
            }
        } else if (option === 'all') {
            if (currentPageCheckbox) {
                setCurrentPageCheckbox(false);
            }
            if (!areAllAccountsSelected) {
                selectAllAccounts();
                setAllPagesCheckbox(true);
            } else if (areAllAccountsSelected && !allPagesCheckbox) {
                setAllPagesCheckbox(true);
            } else {
                deselectAllAccounts();
                setAllPagesCheckbox(false);
            }
        }
    };
    const selectCurrentPageAccounts = () => {
        const allAccountIds = [];
        props.filteredAccounts.forEach((material) => {
            const materialAccountDetails = props.accountDetailsByMaterial.find((idx) => idx.material_id === material.id);
            if (materialAccountDetails) {
                materialAccountDetails.accounts.forEach((account, index) => {
                    allAccountIds.push({
                        accountId: index,
                        materialId: material.id,
                    });
                });
            }
        });

        const updatedSelectedAccounts = [...allAccountIds];
        allAccountIds.forEach((newAccount) => {
            const isAlreadySelected = updatedSelectedAccounts.some((existingAccount) =>
                existingAccount.accountId === newAccount.accountId && existingAccount.materialId === newAccount.materialId);
            if (!isAlreadySelected) {
                updatedSelectedAccounts.push(newAccount);
            }
        });

        props.setSelectedAccounts(updatedSelectedAccounts);
    };

    const deselectCurrentPageAccounts = () => {
        const remainingSelectedAccounts = props.selectedAccounts.filter((selectedAccount) =>
            !props.filteredAccounts.some((material) => material.id === selectedAccount.materialId),
        );

        props.setSelectedAccounts(remainingSelectedAccounts);
    };

    const selectAllAccounts = () => {
        const newSelectedAccounts = [...props.selectedAccounts];
        props.accountDetailsByMaterial.forEach((idx) => {
            idx.accounts.forEach((account, index) => {
                const newAccount = {
                    accountId: index,
                    materialId: idx.material_id,
                };

                const isAlreadySelected = newSelectedAccounts.some((existingAccount) =>
                    existingAccount.accountId === newAccount.accountId && existingAccount.materialId === newAccount.materialId);

                if (!isAlreadySelected) {
                    newSelectedAccounts.push(newAccount);
                }
            });
        });

        props.setSelectedAccounts(newSelectedAccounts);
    };

    const deselectAllAccounts = () => {
        props.setSelectedAccounts([]);
    };

    return (
        <div>
            <IconButton
                onClick={toggleMenu}
                disabled={bulkSelectDisabled}
                sx={{
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                    '& .MuiTouchRipple-root': {
                        display: 'none',
                    },
                }}>
                <TwoToneCheckbox
                    checked={areAllAccountsSelected}
                    indeterminate={!areAllAccountsSelected && uniqueSelectedAccounts.size > 0}
                    disabled={bulkSelectDisabled}
                />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <TwoToneCheckbox
                                    checked={currentPageCheckbox}
                                    onChange={() => handleNestedCheckboxChange('current')}
                                    disabled={bulkSelectDisabled}
                                />
                            }
                            label="Select accounts in displayed page"
                        />
                    </FormGroup>
                </MenuItem>
                <Divider />
                <MenuItem>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <TwoToneCheckbox
                                    checked={allPagesCheckbox}
                                    onChange={() => handleNestedCheckboxChange('all')}
                                    disabled={bulkSelectDisabled}
                                />
                            }
                            label="Select accounts in all pages"
                        />
                    </FormGroup>
                </MenuItem>
            </Menu>
        </div>
    );
}

CheckboxWithOptionsAccountDetails.propTypes = {
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    setSelectedAccounts: PropTypes.func.isRequired,
    selectedAccounts: PropTypes.array.isRequired,
    filteredAccounts: PropTypes.array.isRequired,
    accountDetailsByMaterial: PropTypes.array.isRequired,
};
